import type { MetricWindow, MetricWindowParams, MetricWindowPreset } from '@/api';

export const METRIC_WINDOW_STORAGE_KEY = 'lw.dashboard.metric-window-preset';
export const METRIC_WINDOW_PRESETS: { days: MetricWindowPreset; label: string }[] = [
  { days: 7, label: '7 วัน' },
  { days: 14, label: '14 วัน' },
  { days: 30, label: '30 วัน' },
  { days: 90, label: '90 วัน' },
];
export const DEFAULT_METRIC_WINDOW: MetricWindowPreset = 30;
export const MAX_METRIC_WINDOW_DAYS = 90;

export type MetricWindowSelection =
  | { kind: 'preset'; days: MetricWindowPreset }
  | { kind: 'custom'; from: string; to: string };

export function isMetricWindowPreset(value: unknown): value is MetricWindowPreset {
  return value === 7 || value === 14 || value === 30 || value === 90;
}

export function todayICT(): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

/** Builds an explicit inclusive month-to-date window in ICT calendar dates. */
export function mtdMetricWindowParams(today = todayICT()): Pick<MetricWindowParams, 'from' | 'to'> {
  return { from: `${today.slice(0, 7)}-01`, to: today };
}

/** Returns whether a string is a real calendar date in the API's date format. */
export function isValidMetricWindowDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1) return false;

  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = month === 2
    ? leapYear ? 29 : 28
    : [4, 6, 9, 11].includes(month) ? 30 : 31;
  return day <= daysInMonth;
}

export function shiftDate(date: string, days: number): string {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

export function customMetricWindowValid(from: string, to: string, today = todayICT()): boolean {
  if (!isValidMetricWindowDate(from) || !isValidMetricWindowDate(to) || !isValidMetricWindowDate(today)) return false;
  if (from > to || to > today) return false;
  const span = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
  return span >= 1 && span <= MAX_METRIC_WINDOW_DAYS;
}

export function selectionToParams(selection: MetricWindowSelection): MetricWindowParams {
  return selection.kind === 'preset'
    ? { days: selection.days }
    : { from: selection.from, to: selection.to };
}

export function effectiveWindowLabel(window: MetricWindow | null | undefined): string {
  if (!window) return `${DEFAULT_METRIC_WINDOW} วัน`;
  if (window.from && window.to && window.days !== DEFAULT_METRIC_WINDOW) {
    return window.days === 7 || window.days === 14 || window.days === 90
      ? `${window.days} วัน`
      : 'กำหนดเอง';
  }
  return `${window.days} วัน`;
}

function queryValue(value: unknown): string {
  return Array.isArray(value) ? String(value[0] ?? '') : String(value ?? '');
}

export interface MetricWindowQueryResult {
  selection: MetricWindowSelection;
  invalidExplicit: boolean;
}

/** Parses a route query and records whether an explicit window had to fall back. */
export function selectionFromQueryResult(
  query: Record<string, unknown>,
  fallback: MetricWindowSelection = { kind: 'preset', days: DEFAULT_METRIC_WINDOW },
): MetricWindowQueryResult {
  const daysPresent = query.days != null;
  const customPresent = query.from != null || query.to != null;
  const daysRaw = queryValue(query.days);
  const from = queryValue(query.from);
  const to = queryValue(query.to);
  if (customPresent) {
    const valid = customMetricWindowValid(from, to);
    return {
      selection: valid ? { kind: 'custom', from, to } : fallback,
      invalidExplicit: !valid,
    };
  }
  if (daysPresent) {
    const days = Number(daysRaw);
    const valid = isMetricWindowPreset(days);
    return {
      selection: valid ? { kind: 'preset', days } : fallback,
      invalidExplicit: !valid,
    };
  }
  return { selection: fallback, invalidExplicit: false };
}

/** Parses a route query; invalid values intentionally fall back to the caller's fallback. */
export function selectionFromQuery(
  query: Record<string, unknown>,
  fallback: MetricWindowSelection = { kind: 'preset', days: DEFAULT_METRIC_WINDOW },
): MetricWindowSelection {
  return selectionFromQueryResult(query, fallback).selection;
}
